/**
 * web/src/components/approvals/RegenerateDialog.tsx
 * Confirm → run → summary dialog for regenerating waiting speaker email
 * drafts, one or many. "Regenerate" here means a FRESH REDRAFT: the server
 * recomposes each gate from the event's current templates and defaults,
 * exactly as a brand-new gate open would, and re-persists it onto the still
 * waiting_approval step. The reviewer's edits to those drafts are discarded
 * by design (that's the point — the defaults were wrong when they opened).
 *
 * Safety contract (the operator's hard requirement, 2026-09):
 *   - Nothing is sent. The only send path on the platform is gate approval,
 *     and this dialog never approves anything.
 *   - No notification fires. The engine's regenerate path re-persists the gate
 *     without a gate_opened broadcast or a Slack "ready for review" ping.
 *   - The workflow does not advance; the gates stay in the approvals list.
 *
 * Mechanics: the bulk pass is a client-side loop over the EXISTING single-gate
 * regenerate endpoint (per-workflow lock, role + event-access checks, audit
 * row per gate — all unchanged), a few in flight at a time, with live progress
 * and a per-item failure list. Regenerate is idempotent, so a half-finished
 * pass (tab closed) is simply re-run. On success each gate's localStorage
 * draft is cleared (a surviving draft would win over the fresh server copy —
 * F14) and the pending-gates + workflow queries are invalidated.
 *
 * Background mode (2026-09, `background` prop — Speaker Approvals' bulk
 * buttons): a multi-draft confirm hands the whole set to a SERVER-side batch
 * run (action `regenerate`, pixel.api.speakers) and closes immediately —
 * progress shows in the persistent run dock, the operator keeps working, and
 * a closed tab no longer stops the pass. Same engine call per gate, same
 * nothing-sent guarantee. A single draft still runs inline (instant result).
 */
import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useOptionalBatchRun } from '../speaker/BatchRunContext';
import { regenerateGate } from '../../api/gates';
import { clearDraft, key as draftKey } from '../../lib/forms/draft';
import { qk } from '../../lib/queryKeys';
import { ApiClientError } from '../../lib/api';
import { Icon } from '../../lib/icons';
import { Button } from '../shared/Button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

/** One gate to regenerate, plus the label the summary shows for it. */
export interface RegenerateTarget {
  workflowId: string;
  stepIndex: number;
  /** e.g. "Send logistics email · Jane Doe" */
  label: string;
}

/** Per-target outcome handed back on close. */
export interface RegenerateOutcome {
  workflowId: string;
  stepIndex: number;
  ok: boolean;
  error?: string;
}

/** How many regenerate requests run at once (each rebuild reads a sheet). */
const CONCURRENCY = 3;

type Phase = 'confirm' | 'running' | 'done';

export interface RegenerateDialogProps {
  /** The gates to regenerate. `null` = closed. */
  targets: RegenerateTarget[] | null;
  /** Authenticated user id — scopes the localStorage draft keys to clear. */
  userId: string | undefined;
  /** Called on every close path with the outcomes (empty if cancelled). */
  onClose: (outcomes: RegenerateOutcome[]) => void;
  /** Run a multi-draft set as a background server run (dock progress) instead
   * of an in-dialog loop. Ignored for one draft or outside a batch provider. */
  background?: boolean;
}

/**
 * The regenerate dialog. Opens in the confirm phase, runs on confirm, ends on
 * a summary. Closing mid-run is blocked (the requests are already in flight).
 *
 * @param targets gates to regenerate (null closes the dialog)
 * @param userId scopes the draft keys cleared on success
 * @param onClose receives the outcomes (empty array when cancelled)
 * @sideEffects HTTP POST per gate; clears localStorage drafts; invalidates
 *   the pending-gates + per-workflow queries
 * @idempotent yes — re-running yields the same fresh drafts
 */
export function RegenerateDialog({
  targets,
  userId,
  onClose,
  background = false,
}: RegenerateDialogProps) {
  const queryClient = useQueryClient();
  const batch = useOptionalBatchRun();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [outcomes, setOutcomes] = useState<RegenerateOutcome[]>([]);
  const outcomesRef = useRef<RegenerateOutcome[]>([]);
  const open = targets != null;
  const total = targets?.length ?? 0;
  const single = total === 1;
  const inBackground = background && !single && batch != null;

  const runInBackground = useCallback(async () => {
    if (!targets || targets.length === 0 || batch == null) return;
    const started = await batch.startBackgroundRun(
      'regenerate',
      targets.map((t) => ({
        itemId: `${t.workflowId}:${t.stepIndex}`,
        name: t.label,
        org: '',
        params: { workflowId: t.workflowId, stepIndex: String(t.stepIndex) },
      })),
    );
    if (!started) return; // toast already shown; stay on the confirm
    toast.success(
      `Regenerating ${targets.length} drafts in the background — follow along bottom-right. Nothing is sent.`,
    );
    onClose([]);
  }, [targets, batch, onClose]);

  const run = useCallback(async () => {
    if (!targets || targets.length === 0) return;
    setPhase('running');
    const queue = [...targets];
    const workflowIds = new Set<string>();

    const worker = async () => {
      for (;;) {
        const t = queue.shift();
        if (!t) return;
        let outcome: RegenerateOutcome;
        try {
          await regenerateGate(t.workflowId, t.stepIndex, { selected: [], redraft: true });
          if (userId) clearDraft(draftKey(t.workflowId, t.stepIndex, userId));
          outcome = { workflowId: t.workflowId, stepIndex: t.stepIndex, ok: true };
        } catch (err) {
          outcome = {
            workflowId: t.workflowId,
            stepIndex: t.stepIndex,
            ok: false,
            error:
              err instanceof ApiClientError
                ? err.message
                : err instanceof Error
                  ? err.message
                  : 'Regenerate failed',
          };
        }
        workflowIds.add(t.workflowId);
        outcomesRef.current = [...outcomesRef.current, outcome];
        setOutcomes(outcomesRef.current);
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker));

    // Fresh server copies BEFORE the summary shows: the list re-derives and an
    // open review re-seeds from the refetched gateData, so a caller reacting to
    // onClose (dropping a stashed draft, remounting its form) sees fresh data.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.gates.root() }),
      ...[...workflowIds].map((id) => queryClient.invalidateQueries({ queryKey: qk.workflow.root(id) })),
    ]).catch(() => undefined);
    setPhase('done');
  }, [targets, userId, queryClient]);

  const succeeded = outcomes.filter((o) => o.ok).length;
  const failed = outcomes.filter((o) => !o.ok);
  const labelFor = (o: RegenerateOutcome) =>
    targets?.find((t) => t.workflowId === o.workflowId && t.stepIndex === o.stepIndex)?.label ??
    `${o.workflowId}:${o.stepIndex}`;

  // Every close path resets to the confirm phase so the next open starts fresh
  // (the dialog always closes before it can re-open on another target set).
  const close = () => {
    const result = phase === 'done' ? outcomesRef.current : [];
    setPhase('confirm');
    setOutcomes([]);
    outcomesRef.current = [];
    onClose(result);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Mid-run the requests are already in flight — keep the dialog up so
        // the operator sees the outcome instead of guessing.
        if (!next && phase !== 'running') close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {phase === 'done'
              ? failed.length === 0
                ? single
                  ? 'Draft regenerated'
                  : `${succeeded} draft${succeeded === 1 ? '' : 's'} regenerated`
                : 'Regenerate finished with errors'
              : single
                ? 'Regenerate this draft?'
                : `Regenerate ${total} drafts?`}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          {phase === 'confirm' && (
            <>
              <p className="mb-3 text-text">
                {single ? 'This draft' : 'Each draft'} will be rebuilt from the event&apos;s current
                templates and defaults, exactly as if the gate had just opened. Any edits made to{' '}
                {single ? 'this draft' : 'these drafts'} are discarded.
              </p>
              <p className="rounded-md border border-border bg-bg-soft px-3 py-2 text-[12px] text-text-muted">
                <strong className="font-semibold text-text">Nothing is sent and no one is notified.</strong>{' '}
                The {single ? 'email stays' : 'emails stay'} here for review — the workflow does not
                advance.
              </p>
            </>
          )}

          {phase === 'running' && (
            <div role="status" aria-live="polite">
              <div className="mb-2 flex items-center justify-between text-[13px]">
                <span className="text-text">Regenerating…</span>
                <span className="tabular-nums text-text-muted">
                  {outcomes.length} / {total}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-bg-muted">
                <div
                  className="h-full rounded-full bg-accent transition-[width] duration-200"
                  style={{ width: `${total > 0 ? Math.round((outcomes.length / total) * 100) : 0}%` }}
                />
              </div>
              <p className="mt-3 text-[12px] text-text-subtle">Nothing is being sent.</p>
            </div>
          )}

          {phase === 'done' && (
            <>
              <p className="mb-3 text-text">
                {succeeded > 0 && (
                  <>
                    <strong className="font-semibold">{succeeded}</strong> regenerated from the current
                    defaults and {succeeded === 1 ? 'is' : 'are'} back in the list for review.{' '}
                  </>
                )}
                Nothing was sent.
              </p>
              {failed.length > 0 && (
                <div className="rounded-lg border border-failed-border bg-failed-soft/40 px-3 py-2">
                  <div className="mb-1 text-[12px] font-semibold text-failed">
                    {failed.length} could not be regenerated — {failed.length === 1 ? 'it keeps' : 'they keep'}{' '}
                    the previous draft
                  </div>
                  <ul className="flex flex-col gap-1 text-[12px] text-text">
                    {failed.map((o) => (
                      <li key={`${o.workflowId}:${o.stepIndex}`} className="flex items-start gap-2">
                        <Icon name="alert" size={12} className="mt-0.5 shrink-0 text-failed" />
                        <span>
                          <span className="font-medium">{labelFor(o)}</span>
                          {o.error && <span className="text-text-muted"> — {o.error}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {phase === 'confirm' && (
            <>
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={batch?.starting}
                onClick={() => void (inBackground ? runInBackground() : run())}
              >
                <Icon name="retry" size={12} />
                {single ? 'Regenerate' : `Regenerate ${total}`}
              </Button>
            </>
          )}
          {phase === 'running' && (
            <Button variant="default" disabled>
              <Icon name="retry" size={12} className="animate-spin" />
              Working…
            </Button>
          )}
          {phase === 'done' && (
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
